import React from 'react';
import axios from 'axios';
import {SubmissionError} from 'redux-form';
import {flashMessage} from 'redux-flash';
import classNames from 'classnames';
import {I18n} from 'react-i18nify';
import {store} from '../index';
import SubBarSubItem from '../components/UI/SubBarSubItem';
import Config from '../config';
import {classNamesPrefixed} from '../../helpers/CoreHelpers'
import {scrollToTop} from '../store/actions/layout';
import _ from 'lodash';

export function formValidation(fieldsToValidate, values, dispatch) {
    const errors = {};

    _.each(fieldsToValidate, field => {
        _.each(field.rules, rule => {
            if (rule.validation.function(values[field.key])) {
                errors[field.key] = rule.message;
            }
        });
    });

    if (!_.isEmpty(errors)) {
        dispatch(flashMessage('Submission error! Please check errors!', {
            timeout: 6000,
            props: {
                type: 'error'
            }
        }));

        dispatch(scrollToTop(true));

        throw new SubmissionError(errors);
    }
}

export function filterPages(data, search, contentDefinitionKey) {
    const result = [];

    for (let element of data) {
        let match = true;
        let hidden = false;

        if (search && search !== null && contentDefinitionKey && contentDefinitionKey !== null) {
            let searchMatch = _.includes(element.title.toLowerCase(), search.toLowerCase());
            let filterMatch = element.contentDefinitionKey === contentDefinitionKey;

            match = searchMatch === true && filterMatch === true;
        } else {
            if (search && search !== null) {
                match = _.includes(element.title.toLowerCase(), search.toLowerCase());
            }

            if (contentDefinitionKey && contentDefinitionKey !== null) {
                match = element.contentDefinitionKey === contentDefinitionKey;
            }
        }

        if (match === false && element.children.length === 0) {
            hidden = true;
        }

        if (match === false && hidden === false) {
            const children = filterPages(element.children, search, contentDefinitionKey);

            if (children.length === 0) {
                hidden = true;
            }
        }

        if (hidden === false) {
            const item = {
                ...element,
                children: []
            };

            if (element.children.length > 0) {
                item.children = filterPages(element.children, search, contentDefinitionKey);

                if (item.children.length > 0) {
                    item.expanded = true;
                }
            }

            result.push(item);
        }
    }

    return result
}

export function GenerateRecursiveChildsWithParams(data, parentWorkspaceId, onClick, selected, search = false, blockType, level = 1) {
    const results = [];

    data.forEach((item) => {
        let itemSelected = false;
        let disabled = false;
        let hidden = false;
        let match = false;

        if (_.isArray(selected)) {
            itemSelected = _.find(selected, {contentId: item.id});
        } else {
            itemSelected = selected === item.id;
        }

        if (search) {
            match = _.includes(item.title.toLowerCase(), search.toLowerCase());

            if (match) {
                disabled = false;
            } else {
                disabled = true;
            }
        }

        if (search) {
            if (disabled) {
                if (item.children.length === 0) {
                    hidden = true;
                }
            }
        }

        results.push(
            <li key={item.id}>
                <div onClick={(e) => {
                    e.preventDefault();

                    onClick(parentWorkspaceId, item.id, item.title);
                }}
                     className={classNamesPrefixed({
                         'item': true,
                         'match': match,
                         'selected': itemSelected,
                         'disabled': disabled,
                         'hidden': hidden,
                         ['level-' + level]: true
                     })}
                >
                    {item.title} ({item.contentDefinitionKey})
                </div>
                <ul>
                    {GenerateRecursiveChildsWithParams(item.children, parentWorkspaceId, onClick, selected, search, blockType, level + 1)}
                </ul>
            </li>
        );
    });

    return results;
}

export function GenerateRecursiveChilds(data, parentWorkspaceId, pathname, props, type, level = 1) {
    const results = [];
    const language = store.getState().language.current;

    data.forEach((item) => {
        const title = (
            <a
                href={fixUrl('#/workspace/' + parentWorkspaceId + '/' + type + '/' + item.id + '/' + language)}
                className={classNamesPrefixed({
                    ['level-' + level]: true,
                    'nav-link': true,
                    'active': isActiveMenu(pathname, '/workspace/' + parentWorkspaceId + '/' + type + '/' + item.id + '/' + language)
                })}
            >
                {item.title}
            </a>
        );

        const child = GenerateRecursiveChilds(item.children, parentWorkspaceId, pathname, props, type, level + 1);

        results.push(
            <SubBarSubItem
                item={item}
                title={title}
                key={item.id}
                {...props}
                id={item.id}
                section={type}
                expandable={child.length > 0}
            >
                {child}
            </SubBarSubItem>
        );
    });

    return results;
}

export function GetFlatArrayFromRecursive(data, level = 1, results = []) {
    data.forEach((content) => {
        results.push(content);

        if (content.children && content.children.length > 0) {
            GetFlatArrayFromRecursive(content.children, level + 1, results);
        }
    });

    return results;
}

export function GetArrayFromRecursive(data, level = 1, results = []) {
    let prefix = '';

    for (let i = 0; i < level; i++) {
        prefix += '-';
    }

    data.forEach((content) => {
        results.push({
            id: content.id,
            title: prefix + ' ' + content.title
        });

        if (content.children && content.children.length > 0) {
            GetArrayFromRecursive(content.children, level + 1, results);
        }
    });

    return results;
}

export async function GenerateRelatedContents(values) {
    const data = {};

    if (values) {
        if (values.AreaBlocks) {
            await Promise.all(values.AreaBlocks.map(async (id) => {
                const response = await axios.get(Config.api.endpoint + 'api/content/' + id + '/' + values.languageId,Config.getApiConfig());

                if (response.data) {
                    data[id] = response.data;
                }
            }));
        }

        if (values.TableBlock) {
            await Promise.all([values.TableBlock].map(async (id) => {
                const response = await axios.get(Config.api.endpoint + 'api/content/' + id + '/' + values.languageId,Config.getApiConfig());

                if (response.data) {
                    data[id] = response.data;
                }
            }));
        }

        if (values.LevelDescBlock) {
            await Promise.all([values.LevelDescBlock].map(async (id) => {
                const response = await axios.get(Config.api.endpoint + 'api/content/' + id + '/' + values.languageId,Config.getApiConfig());

                if (response.data) {
                    data[id] = response.data;
                }
            }));
        }

        if (values.BlockArea) {
            await Promise.all(values.BlockArea.map(async (id) => {
                const response = await axios.get(Config.api.endpoint + 'api/content/' + id + '/' + values.languageId,Config.getApiConfig());

                if (response.data) {
                    data[id] = response.data;
                }
            }));
        }

        if (values.SliderBlockArea) {
            await Promise.all(values.SliderBlockArea.map(async (id) => {
                const response = await axios.get(Config.api.endpoint + 'api/content/' + id + '/' + values.languageId,Config.getApiConfig());

                if (response.data) {
                    data[id] = response.data;
                }
            }));
        }

        if (values.MyBlock) {
            await Promise.all([values.MyBlock].map(async (id) => {
                const response = await axios.get(Config.api.endpoint + 'api/content/' + id + '/' + values.languageId,Config.getApiConfig());

                if (response.data) {
                    data[id] = response.data;
                }
            }));
        }

        if (values.SomeBlocks) {
            await Promise.all(values.SomeBlocks.map(async (id) => {
                const response = await axios.get(Config.api.endpoint + 'api/content/' + id + '/' + values.languageId,Config.getApiConfig());

                if (response.data) {
                    data[id] = response.data;
                }
            }));
        }

        if (values.IconBlocks) {
            await Promise.all(values.IconBlocks.map(async (id) => {
                const response = await axios.get(Config.api.endpoint + 'api/content/' + id + '/' + values.languageId,Config.getApiConfig());

                if (response.data) {
                    data[id] = response.data;
                }
            }));
        }

        if (values.TextBlocks) {
            await Promise.all(values.TextBlocks.map(async (id) => {
                const response = await axios.get(Config.api.endpoint + 'api/content/' + id + '/' + values.languageId,Config.getApiConfig());

                if (response.data) {
                    data[id] = response.data;
                }
            }));
        }
    }

    return data;
}

export function ProcessRelatedContents(fields, results = []) {
    if (fields) {
        _.each(fields, (field) => {
            results[field.id] = ProcessContentFields(field.contentFields);

            if (field.relatedContents && field.relatedContents.length) {
                ProcessRelatedContents(field.relatedContents, results);
            }
        });
    }

    return results;
}

export function ProcessContentFields(fields) {
    const values = {};

    if (fields) {
        fields.forEach((field) => {
            try {
                values[field.fieldDefinitionKey] = JSON.parse(field.data);
            } catch (e) {
                values[field.fieldDefinitionKey] = field.data;
            }
        });
    }

    return values;
}

export function ProcessGlobalFields(relatedContents, results = []) {
    if (relatedContents) {
        _.each(relatedContents, (relatedContent) => {
            if (relatedContent.globalFieldData && relatedContent.globalFieldData.length > 0) {
                _.each(relatedContent.globalFieldData, globalFieldData => {
                    results[globalFieldData.key] = [];

                    try {
                        results[globalFieldData.key]['data'] = JSON.parse(globalFieldData.data);
                    } catch (e) {
                        results[globalFieldData.key]['data'] = globalFieldData.data;
                    }

                    if (globalFieldData.relatedContents) {
                        results[globalFieldData.key]['related'] = ProcessRelatedContents(globalFieldData.relatedContents);
                    } else {
                        results[globalFieldData.key]['related'] = [];
                    }
                });
            }
        });
    }

    return results;
}

export function ProcessContentValuesCreate(values, fieldDefinitions) {
    if (values) {
        const data = {};

        data.contentFields = [];

        if (fieldDefinitions) {
            fieldDefinitions.forEach((field) => {
                if (values[field.key]) {
                    if (typeof values[field.key] !== 'string') {
                        data.contentFields.push({
                            'fieldDefinitionKey': field.key,
                            'data': JSON.stringify(values[field.key])
                        });
                    } else {
                        data.contentFields.push({
                            'fieldDefinitionKey': field.key,
                            'data': values[field.key]
                        });
                    }
                }
            });
        }

        return data;
    } else {
        return [];
    }
}

export function ProcessContentValuesEdit(values, fieldDefinitions) {
    const fields = [];

    if (fieldDefinitions) {
        fieldDefinitions.forEach((field) => {
            if (values[field.key]) {
                if (typeof values[field.key] !== 'string') {
                    fields.push({
                        'fieldDefinitionKey': field.key,
                        'data': JSON.stringify(values[field.key])
                    });
                } else {
                    fields.push({
                        'fieldDefinitionKey': field.key,
                        'data': values[field.key]
                    });
                }
            }
        });
    }

    return fields;
}

export function GetContentTitle(values) {
    if (values && values.contentFields) {
        const field = _.find(values.contentFields, {fieldDefinitionKey: 'Title'});

        if (field) {
            return field.data;
        }
    }
}

export function GetContentType(values, type) {
    if (type === 'page' && values.contentDefinitionKey) {
        return I18n.t('content.' + values.contentDefinitionKey);
    }

    if (type === 'block' && values.contentDefinitionKey) {
        return I18n.t('block.' + values.contentDefinitionKey);
    }

    return '';
}

export function GetContent(values) {
    let content = [];

    if (values && values.contentFields) {
        _.each(values.contentFields, (contentField, index) => {
            if (contentField.fieldDefinitionKey !== 'Title') {
                content.push(<div key={index} dangerouslySetInnerHTML={{__html: contentField.data}}/>)
            }
        });
    }

    return content;
}

export function isActiveMenu(currentPath, path, startsWith = false, pageTitle) {
    let isActive = false;

    if (startsWith) {
        isActive = currentPath.startsWith(startsWith);
    } else {
        isActive = currentPath === path;
    }

    if (isActive){
        if (pageTitle && pageTitle!==document.title){
            document.title = pageTitle;
        }
    }
    return isActive;
}

export function fixUrl(url) {
    return url;
}

export function GetFileExtension(filename) {
    if (filename) {
        return filename.split(".").pop();
    }
}

export function GetFileType(fileName) {
    let fExt = GetFileExtension(fileName).toLowerCase();
    switch (fExt) {
        case "jpg":
        case "png":
        case "gif":
        case "jpeg":
        case "svg":
            return {
                id: "images",
                title: "Images",
                ext:fExt
            };
        case "pdf":
        case "doc":
        case "docx":
            return {
                id: "documents",
                title: "Documents",
                ext:fExt,
            };
        case "txt":
            return {
                id: "txt",
                title: "Text files",
                ext: fExt,
            };
        case "zip":
        case "rar":
        case "gzip":
        case "tar":
            return {
                id: "archives",
                title: "Archives",
                ext:fExt
            };
        case "xml":
            return {
                id: "xml",
                title: "Xml",
                ext:fExt,
            };
        default:
            return {
                id: "unknown-file-type",
                title: "Unknown file type",
                ext:fExt,
            };
    }
}

export function IsFileTypeInList(fileType, fileTypes) {
    return fileTypes.find((ft) => {
        return ft.id === fileType.id;
    });
}

export function GetFileTypes(fileList) {
    let fileTypes = [];
    let x = null;
    let fileType = null;

    if (fileList && fileList.length > 0) {
        for (x in fileList) {
            fileType = GetFileType(fileList[x].fileName);

            if (fileTypes.length === 0 || !IsFileTypeInList(fileType, fileTypes)) {
                fileTypes.push({id: fileType.id, title: fileType.title});
            }
        }
    }

    return fileTypes;
}

export function mediaPreviewUrl(imageObject) {
    return Config.api.getCdnEndPoint() + "api/media/" + imageObject.ref + "/preview";
}

export function mediaUrl(imageObject) {
    return Config.api.getCdnEndPoint() + "api/media/" + imageObject.ref + "/file";
}

export function mediaDownloadUrl(imageObject) {
    return Config.api.getCdnEndPoint() + "api/media/" + imageObject.ref + "/file?download=1";
}

export function normalizeItems(items, parentId = null) {
    const result = [];

    for (let element of items) {
        const item = {
            id: element.id,
            parentId: parentId,
            title: element.title,
            children: []
        };

        if (element.children.length > 0) {
            item.children = normalizeItems(element.children, element.id)
        }

        result.push(item)
    }

    return result
}

export function findItem(items, id) {
    let result = null;

    for (let element of items) {
        if (element.id === id) {
            result = {
                id: element.id,
                parentId: element.parentId,
                title: element.title
            };

            break
        }

        if (element.children.length > 0) {
            result = findItem(element.children, id);

            if (result) {
                break;
            }
        }
    }

    return result
}

export function createActiveMenuArray(items, itemId, result = []) {
    const item = findItem(items, itemId);

    if (item) {
        result.push(item.id);

        if (item.parentId) {
            createActiveMenuArray(items, item.parentId, result)
        }
    }
    return result
}


export function getPageUrl(id){
    return Config.frontURL + '#/page/' + id;
}


export function isLogged(){
    //todo this have to reimplemented and tested
    //return true;
    return Config.getToken() ? true : false;
}

export function isAdmin(){
    return isLogged();
}

export const collapsibleSettings = {
    transitionTime: 150,
    easing: 'ease-out',
    classParentString: 'collapsible'
}


// WEBPACK FOOTER //
// ./src/core/admin/helpers/Helpers.js